import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementChangeEvent } from "@stripe/stripe-js";
import { useCallback, useRef, useState } from "react";
import { CardFieldRow, CardFields, ErrorText, StripeField } from "./styles";

const stripeStyle = {
  base: {
    "::placeholder": { color: "#a3a3a4" },
    color: "#19191b",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "400",
    backgroundColor: "#ffffff",
  },
};

type CardPart = "number" | "expiry" | "cvc";

interface CardFormProps {
  /** Stripe paymentMethod id once the card is complete & valid, else null. */
  onPaymentMethodChange: (id: string | null) => void;
}

/**
 * Credit-card fields via Stripe Elements. When all three fields are complete we
 * create a Stripe paymentMethod and report its id up; any later edit clears it
 * (so the id never goes stale). Raw card data never enters app state.
 */
export const CardForm = ({ onPaymentMethodChange }: CardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const completeRef = useRef<Record<CardPart, boolean>>({
    number: false,
    expiry: false,
    cvc: false,
  });
  const [error, setError] = useState<string | null>(null);

  const tokenize = useCallback(async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardNumberElement);
    if (!card) return;
    const result = await stripe.createPaymentMethod({ type: "card", card });
    if (result.error || !result.paymentMethod) {
      setError(result.error?.message ?? "Could not validate the card.");
      onPaymentMethodChange(null);
      return;
    }
    setError(null);
    onPaymentMethodChange(result.paymentMethod.id);
  }, [stripe, elements, onPaymentMethodChange]);

  const handleChange = useCallback(
    (part: CardPart) => (event: StripeElementChangeEvent) => {
      completeRef.current[part] = event.complete && !event.error;
      const { number, expiry, cvc } = completeRef.current;
      if (number && expiry && cvc) {
        void tokenize();
      } else {
        // Any incomplete/edited field invalidates a previously-produced token.
        setError(null);
        onPaymentMethodChange(null);
      }
    },
    [tokenize, onPaymentMethodChange],
  );

  return (
    <CardFields data-testid="card-form">
      <StripeField data-testid="card-number">
        <CardNumberElement
          options={{ style: stripeStyle, placeholder: "1234 1234 1234 1234" }}
          onChange={handleChange("number")}
        />
      </StripeField>
      <CardFieldRow>
        <StripeField data-testid="card-expiry">
          <CardExpiryElement
            options={{ style: stripeStyle, placeholder: "12 / 24" }}
            onChange={handleChange("expiry")}
          />
        </StripeField>
        <StripeField data-testid="card-cvc">
          <CardCvcElement
            options={{ style: stripeStyle, placeholder: "CVC" }}
            onChange={handleChange("cvc")}
          />
        </StripeField>
      </CardFieldRow>
      {error && (
        <ErrorText role="alert" data-testid="card-error">
          {error}
        </ErrorText>
      )}
    </CardFields>
  );
};
